import { useApolloClient, useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { RecordFolder } from 'src/graphql';

import { DELETE_RECORD_FOLDERS_QUERY } from 'src/graphql/entities/record-folder/record-folder.operations';

export interface DeleteRecordFolder {
  deleteRecordFolderWithAssignments: Pick<RecordFolder, 'id'> & {
    __typename: 'RecordFolder';
  };
}

export const useDeleteRecordFolder = () => {
  const client = useApolloClient();
  const [deleteRecordFolder, { loading: isLoading }] = useMutation<DeleteRecordFolder>(
    DELETE_RECORD_FOLDERS_QUERY,
    {
      update(cache, { data }) {
        if (!data?.deleteRecordFolderWithAssignments.id) return;

        const id = cache.identify({
          __typename: 'RecordFolder',
          id: data.deleteRecordFolderWithAssignments.id,
        });

        cache.evict({ id });
        cache.gc();
      },
    },
  );

  const deleteWithOptimisticResponse = useCallback(
    async (recordFolder: RecordFolder) => {
      const omitIsolationModeHeader = recordFolder.partnerId === PLATFORM_PARTNER_ID;
      await deleteRecordFolder({
        variables: {
          params: { recordFolderId: recordFolder.id },
        },
        optimisticResponse: {
          deleteRecordFolderWithAssignments: { __typename: 'RecordFolder', ...recordFolder },
        },
        context: omitIsolationModeHeader ? { omitIsolationModeHeader: true } : undefined,
      });

      if (omitIsolationModeHeader) {
        client.cache.evict({
          fieldName: 'getEnabledRecordFolders',
        });
        client.cache.gc();
      }
    },
    [client, deleteRecordFolder],
  );

  return {
    deleteRecordFolder: deleteWithOptimisticResponse,
    isLoading,
  };
};
